import { add, lessThanOrEqual } from "dinero.js";
import React from "react";
import { Image } from "tsi-common-react/src/components/Image/index";
import { Link } from "tsi-common-react/src/components/Link/index";
import { RawHTML } from "tsi-common-react/src/components/RawHTML/index";
import {
    AddToCartType,
    BundleGroupTypes,
} from "tsi-common-react/src/constants";
import {
    IBasket,
    IBasketLine,
    IConcreteBundle,
    IProduct,
} from "tsi-common-react/src/models/catalogue";
import {
    IProductAPIURL,
    isoMonths,
} from "tsi-common-react/src/models/nominals";
import { trackAddToBasketEvent } from "tsi-common-react/src/utils/analytics";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import {
    type Dinero,
    getDinero,
    getMonthlyPrice,
} from "tsi-common-react/src/utils/money";
import { jt, t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

const scrollToFinePrint = (event: React.MouseEvent<HTMLElement>): void => {
    if (event) {
        event.preventDefault();
    }

    const document = getDocument();
    const finePrint = document?.querySelector("#basket-lines__fine-print");
    finePrint?.scrollIntoView({ behavior: "smooth" });
};

const buildImage = (product: IProduct): React.ReactNode => {
    // Use the product image if one exists else try the parent image
    let image = product.images.slice().shift();
    if (!image && product.parent) {
        image = product.parent.images.slice().shift();
    }
    // TODO: Need link title since content is an image. See #13290
    return image ? (
        <Link href={product.parent?.link || product.link}>
            <Image
                src={image.original}
                className="basket-suggestion__graphic__image"
            />
        </Link>
    ) : null;
};

const buildHeadline = (bundles: readonly IConcreteBundle[]): string => {
    let headline = t`Complete Your Sleep Experience`;

    for (const bundle of bundles) {
        const bundle_type = bundle.bundle_group.bundle_type;
        if (
            (bundle_type === BundleGroupTypes.DEFAULT ||
                bundle_type === BundleGroupTypes.IN_BASKET_ADD_ON) &&
            bundle.bundle_group.headline
        ) {
            headline = bundle.bundle_group.headline;
            break;
        }
    }
    return headline;
};

const buildOptions = (product: IProduct): React.ReactNode => {
    const attributes = product.attributes;
    const optionKeys = attributes.product_options?.value || [];
    if (optionKeys.length <= 0) {
        return null;
    }

    const options = optionKeys.map((key) => {
        const attr = attributes[key];
        if (!attr) {
            return null;
        }
        return (
            <li key={key} className="basket-suggestion__details__option">
                <span>{attr.name}</span>: <strong>{attr.value}</strong>
            </li>
        );
    });

    return (
        <div className="basket-suggestion__details__options">
            <ul>{options}</ul>
        </div>
    );
};

const calcPlanMonths = (total: Dinero): number | null => {
    const plans = listFinancingPlans();
    const availablePlans = plans.filter((plan) => {
        return lessThanOrEqual(plan.threshold, total);
    });
    const bestPlan = availablePlans[availablePlans.length - 1];
    return bestPlan ? isoMonths.unwrap(bestPlan.length) : null;
};

const buildFinancing = (
    product: IProduct,
    basketTotal: Dinero,
): React.ReactNode => {
    if (!product.price.enable_per_month_pricing) {
        return null;
    }

    // Calculate Suggested plan based on (Cart total + Suggestion Product total) / (Highest plan that total qualifies for)
    const basketTotalPrice = getDinero(basketTotal);
    const extraProductPrice = getDinero(
        product.price.cosmetic_excl_tax || "0.00",
    );
    const computedTotalPrice = add(basketTotalPrice, extraProductPrice);
    const planMonths = calcPlanMonths(computedTotalPrice);

    if (!planMonths) {
        return null;
    }

    const computedMonthlyPrice = getMonthlyPrice(
        getDinero(extraProductPrice),
        planMonths,
    );

    const priceSpan = (
        <span
            key="monthly-price"
            className="basket-suggestion__financing--underline"
        >
            {format.money(computedMonthlyPrice)}
        </span>
    );
    const sup2 = <sup key="sup-2">2</sup>;
    const monthsSpan = (
        <span
            key="plan-months"
            className="basket-suggestion__financing--underline"
        >
            {planMonths}
        </span>
    );
    const sup1 = <sup key="sup-1">1</sup>;

    return (
        <p>
            <a onClick={scrollToFinePrint}>
                {jt`or ${priceSpan}/mo.${sup2} for ${monthsSpan} months${sup1}`}
            </a>
        </p>
    );
};

export const BasketLineSuggestion = (props: {
    line: IBasketLine;
    product: IProduct;
    basketTotal: Dinero;
    addBasketLine: (
        url: IProductAPIURL,
        qty: number,
    ) => Promise<IBasket | null>;
}): React.ReactNode => {
    const lineProduct = props.line.product;
    const lineRootProduct = lineProduct.parent || lineProduct;

    const rootProduct = props.product.parent || props.product;

    const handleAddToBasket = async (): Promise<void> => {
        await props.addBasketLine(props.product.url, 1);
        trackAddToBasketEvent(
            props.product,
            1,
            AddToCartType.IN_CART_UP_CROSS_SELL,
        );
    };

    const headline = buildHeadline(props.line.bundles);
    const financing = buildFinancing(props.product, props.basketTotal);

    return (
        <div className="basket-suggestion">
            <div className="basket-suggestion__row basket-suggestion__row--shaded">
                <h4>{headline}</h4>
            </div>
            <div className="basket-suggestion__row">
                <div className="basket-suggestion__graphic">
                    {buildImage(props.product)}
                </div>
                <div className="basket-suggestion__details">
                    <h5>
                        {richT(
                            t`<bestSeller>Best Seller</bestSeller> for ${lineRootProduct.title}`,
                            {
                                bestSeller: (children) => (
                                    <span className="basket-suggestion__best-seller">
                                        {children}
                                    </span>
                                ),
                            },
                        )}
                    </h5>
                    <h4 className="basket-suggestion__details-title">
                        <Link href={rootProduct.link}>{rootProduct.title}</Link>
                    </h4>
                    <RawHTML
                        className="basket-suggestion__details-description"
                        html={rootProduct.description}
                    />
                    {buildOptions(props.product)}
                </div>
                <div className="basket-suggestion__price">
                    <p>{format.money(props.product.price.cosmetic_excl_tax)}</p>
                    <div className="basket-suggestion__financing">
                        {financing}
                    </div>
                    <button
                        className="button al-basket-addtocart-upsell"
                        onClick={handleAddToBasket}
                    >
                        {t`Add to cart`}
                    </button>
                </div>
            </div>
        </div>
    );
};
